import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'
import type { ApiResponse, BookingQuotation, BookingQuotationPayload, PaginatedResponse } from '@/types'

const unwrapData = <T>(payload: ApiResponse<T> | T): T => {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    return (payload as ApiResponse<T>).data
  }
  return payload as T
}

export type QuotationsQueryParams = {
  page?: number
  page_size?: number
  search?: string
  status?: string
  enquiry_id?: string | number | null
  source_followup_id?: string | number | null
  customer_mode?: 'new' | 'existing'
  customer_segment?: 'retail_customer' | 'business_customer'
  contract_attached?: boolean
  service_instance_id?: string | number | null
}

export const quotationsApi = {
  async list(params?: QuotationsQueryParams): Promise<PaginatedResponse<BookingQuotation>> {
    const response = await api.get<ApiResponse<PaginatedResponse<BookingQuotation>> | PaginatedResponse<BookingQuotation>>(
      API_PATHS.store.quotations.list,
      { params },
    )
    return unwrapData(response.data)
  },

  async create(payload: BookingQuotationPayload, serviceInstanceId?: string | number | null): Promise<BookingQuotation> {
    const response = await api.post<ApiResponse<BookingQuotation> | BookingQuotation>(
      API_PATHS.store.quotations.create,
      payload,
      { params: { service_instance_id: serviceInstanceId ?? undefined } },
    )
    return unwrapData(response.data)
  },

  async get(id: string | number, serviceInstanceId?: string | number | null): Promise<BookingQuotation> {
    const response = await api.get<ApiResponse<BookingQuotation> | BookingQuotation>(
      API_PATHS.store.quotations.detail(id),
      { params: { service_instance_id: serviceInstanceId ?? undefined } },
    )
    return unwrapData(response.data)
  },

  async update(id: string | number, payload: BookingQuotationPayload, serviceInstanceId?: string | number | null): Promise<BookingQuotation> {
    const response = await api.patch<ApiResponse<BookingQuotation> | BookingQuotation>(
      API_PATHS.store.quotations.update(id),
      payload,
      { params: { service_instance_id: serviceInstanceId ?? undefined } },
    )
    return unwrapData(response.data)
  },

  async uploadContract(
    id: string | number,
    payload: {
      file: File
      contractDocumentName?: string | null
    },
    serviceInstanceId?: string | number | null,
  ): Promise<BookingQuotation> {
    const formData = new FormData()
    formData.append('contract_document_file', payload.file)
    if (payload.contractDocumentName) {
      formData.append('contract_document_name', payload.contractDocumentName)
    }

    const response = await api.post<ApiResponse<BookingQuotation> | BookingQuotation>(
      API_PATHS.store.quotations.uploadContract(id),
      formData,
      {
        params: { service_instance_id: serviceInstanceId ?? undefined },
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
    return unwrapData(response.data)
  },

  async removeContract(id: string | number, serviceInstanceId?: string | number | null): Promise<BookingQuotation> {
    const response = await api.post<ApiResponse<BookingQuotation> | BookingQuotation>(
      API_PATHS.store.quotations.removeContract(id),
      {},
      { params: { service_instance_id: serviceInstanceId ?? undefined } },
    )
    return unwrapData(response.data)
  },

  async accept(id: string | number, serviceInstanceId?: string | number | null): Promise<BookingQuotation> {
    const response = await api.post<ApiResponse<BookingQuotation> | BookingQuotation>(
      API_PATHS.store.quotations.accept(id),
      {},
      { params: { service_instance_id: serviceInstanceId ?? undefined } },
    )
    return unwrapData(response.data)
  },

  async convert(id: string | number, serviceInstanceId?: string | number | null): Promise<{
    quotation: BookingQuotation
    order_id: string | number
    order_reference?: string | number | null
    created?: boolean
    planner_result?: unknown
  }> {
    const response = await api.post<ApiResponse<{
      quotation: BookingQuotation
      order_id: string | number
      order_reference?: string | number | null
      created?: boolean
      planner_result?: unknown
    }>>(
      API_PATHS.store.quotations.convert(id),
      {},
      { params: { service_instance_id: serviceInstanceId ?? undefined } },
    )
    return unwrapData(response.data)
  },
}
